import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { CheckCircle2, Layers, Timer, Users } from 'lucide-react'
import { CHART, tooltipProps, xAxisProps } from '@/lib/chart'
import { cn } from '@/lib/cn'
import type { OperatorReportRow, StageExtreme } from '@/types'
import { formatMinutes } from '@/lib/duration'
import Card, { SectionLabel } from '@/components/ui/Card'
import StatTile from '@/components/ui/StatTile'
import { ChartBox, ChartEmpty } from '@/components/ui/Chart'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'

const icon = 'size-5'

export default function OperatorsTab({ data, range }: { data: OperatorReportRow[]; range: { from: string; to: string } }) {
  const top = [...data].sort((a, b) => b.pieces_completed - a.pieces_completed).slice(0, 12)
  // A team stage appears on every teammate's row. Each completion has exactly
  // one operator who tapped it, so leaving out the assisted ones counts each
  // completion once - the same number as before teams could be credited.
  const stages = data.reduce((sum, r) => sum + r.stages_completed - (r.stages_assisted ?? 0), 0)
  // Deliberately a per-person sum: a two-person stage credits its pieces to
  // both. The Overview tab is the source of truth for factory output.
  const pieces = data.reduce((sum, r) => sum + r.pieces_completed, 0)

  // Weighted by how many stages each operator was measured on, so somebody
  // with one slow stage does not move the shop average as much as somebody
  // with fifty. A team stage weighs once per person.
  const measured = data.reduce((sum, r) => sum + r.stages_measured, 0)
  const avgMinutes = measured
    ? Math.round(data.reduce((sum, r) => sum + (r.avg_stage_minutes ?? 0) * r.stages_measured, 0) / measured)
    : null

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile label="Operators active" value={data.length} icon={<Users className={icon} />} tone="primary" />
        <StatTile label="Stages completed" value={stages} icon={<CheckCircle2 className={icon} />} tone="success" />
        <StatTile label="Pieces credited" value={pieces} icon={<Layers className={icon} />} tone="pending" />
        <StatTile
          label="Avg time per stage"
          value={formatMinutes(avgMinutes)}
          icon={<Timer className={icon} />}
          tone="primary"
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <Card className="mt-6 min-w-0">
        <SectionLabel>Pieces credited per operator{data.length > top.length ? ` (top ${top.length})` : ''}</SectionLabel>
        {top.length ? (
          <ChartBox height={Math.max(160, top.length * 28 + 24)}>
            <BarChart data={top} layout="vertical" margin={{ left: 4, right: 16 }}>
              <CartesianGrid strokeDasharray="2 2" stroke={CHART.grid} horizontal={false} />
              <XAxis type="number" {...xAxisProps} />
              <YAxis type="category" dataKey="operator" width={110} tick={{ fontSize: 11, fill: CHART.tick }} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="pieces_completed" name="Pieces" fill={CHART.primary} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ChartBox>
        ) : (
          <ChartEmpty height={160} />
        )}
      </Card>

      <SectionLabel className="mt-6">Output per operator ({range.from} to {range.to})</SectionLabel>
      <Table
        columns={[
          'Operator', 'Department', 'Started', 'Completed', 'Assisted', 'Issues', 'Jobs touched', 'Pieces credited',
          'Avg per stage', 'Fastest', 'Slowest', 'Days', 'Avg start', 'Avg end',
        ]}
      >
        {data.map((row) => (
          <Row key={row.operator + (row.department ?? '')}>
            <Cell className="font-semibold">{row.operator}</Cell>
            <Cell>{row.department ?? '-'}</Cell>
            <Cell>{row.stages_started}</Cell>
            <Cell className="font-semibold">{row.stages_completed}</Cell>
            <Cell className={cn((row.stages_assisted ?? 0) === 0 && 'text-muted')}>{row.stages_assisted ?? 0}</Cell>
            <Cell className={cn(row.issues_flagged > 0 && 'font-semibold text-status-danger')}>{row.issues_flagged}</Cell>
            <Cell>{row.jobs_touched}</Cell>
            <Cell className="font-semibold">{row.pieces_completed}</Cell>
            <Cell className="font-semibold">{formatMinutes(row.avg_stage_minutes)}</Cell>
            <Cell><StageCell stage={row.fastest_stage} /></Cell>
            <Cell><StageCell stage={row.slowest_stage} /></Cell>
            <Cell>{row.days_worked}</Cell>
            <Cell className="whitespace-nowrap">{row.avg_start_clock ?? '-'}</Cell>
            <Cell className="whitespace-nowrap">{row.avg_end_clock ?? '-'}</Cell>
          </Row>
        ))}
        {data.length === 0 && <EmptyRow colSpan={14}>No stage activity logged in this date range.</EmptyRow>}
      </Table>
      <p className="mt-2 text-xs text-muted">
        Assisted = completions credited as a teammate on somebody else's stage. Each teammate is credited in full, so
        pieces here add up per person; use the Overview tab for factory totals.
      </p>
    </>
  )
}

/** A measured extreme, with the job it came from so it can be looked up. */
function StageCell({ stage }: { stage: StageExtreme | null }) {
  if (!stage) return <span className="text-muted">-</span>

  return (
    <span className="whitespace-nowrap">
      {formatMinutes(stage.minutes)}
      {stage.job_code && <span className="ml-1 font-mono text-2xs text-muted">{stage.job_code}</span>}
    </span>
  )
}
